import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Folder, ToggleLeft, ToggleRight } from "lucide-react";
import { useEffect, useState } from "react";
import AdminPageLayout from "./AdminPageLayout";
import { apiGet, apiPatch } from "./api";

interface Category {
  id: number;
  name: string;
  slug: string;
  section: string;
  sort_order: number;
  active: boolean;
}

export default function CategoriesManagement() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const data = await apiGet<{ items: Category[] }>("/api/admin/categories");
      setCategories(data.items || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (category: Category) => {
    try {
      await apiPatch(`/api/admin/categories/${category.id}`, {
        active: !category.active,
      });

      setCategories((prev) =>
        prev.map((c) =>
          c.id === category.id ? { ...c, active: !c.active } : c
        )
      );

      toast({
        title: "Category Updated",
        description: `${category.name} is now ${
          !category.active ? "active" : "inactive"
        }`,
      });
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    }
  };

  const sections = Array.from(new Set(categories.map((c) => c.section)));

  return (
    <AdminPageLayout
      title="Categories Management"
      description="Manage content categories and their visibility"
      icon={<Folder className="w-8 h-8" />}
      loading={loading}
      error={error}
    >
      {sections.map((section) => {
        const sectionCategories = categories
          .filter((c) => c.section === section)
          .sort((a, b) => a.sort_order - b.sort_order);

        return (
          <Card key={section}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Folder className="w-5 h-5" />
                {section}
              </CardTitle>
              <CardDescription>
                {sectionCategories.length} categories in this section
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table
                  className="w-full text-sm"
                  data-testid={`table-${section}`}
                >
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 font-medium">ID</th>
                      <th className="text-left p-2 font-medium">Name</th>
                      <th className="text-left p-2 font-medium">Slug</th>
                      <th className="text-left p-2 font-medium">Sort Order</th>
                      <th className="text-left p-2 font-medium">Status</th>
                      <th className="text-left p-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sectionCategories.map((category) => (
                      <tr
                        key={category.id}
                        className="border-b hover-elevate"
                        data-testid={`category-${category.id}`}
                      >
                        <td className="p-2">{category.id}</td>
                        <td className="p-2 font-medium">{category.name}</td>
                        <td className="p-2">
                          <code className="text-xs bg-muted px-2 py-1 rounded">
                            {category.slug}
                          </code>
                        </td>
                        <td className="p-2">{category.sort_order}</td>
                        <td className="p-2">
                          <Badge
                            variant={category.active ? "default" : "secondary"}
                            data-testid={`status-${category.id}`}
                          >
                            {category.active ? "Active" : "Inactive"}
                          </Badge>
                        </td>
                        <td className="p-2">
                          <Button
                            onClick={() => toggleActive(category)}
                            variant="ghost"
                            size="sm"
                            data-testid={`button-toggle-${category.id}`}
                          >
                            {category.active ? (
                              <>
                                <ToggleRight className="mr-1 w-4 h-4" />
                                Deactivate
                              </>
                            ) : (
                              <>
                                <ToggleLeft className="mr-1 w-4 h-4" />
                                Activate
                              </>
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </AdminPageLayout>
  );
}
